'use client'

import * as React from 'react'
import * as Sentry from '@sentry/nextjs'
import { ProductCard } from '@web/components/product-card/product-card'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import type { Locales } from 'cf-i18n/helpers'
import type { Language } from 'codegen/types'
import { useIntersectionObserver } from 'hooks/use-intersection-observer'

import type { SortType } from '../legacy-product-category-page/pagination/sort'
import {
  getMarketplaceProducts,
  getMarketplaceProductsPayload,
  mapMarketplaceProductsToCategoryProducts,
  setSortingOptions,
} from './marketplace'
import type { MarketplaceCategoryProduct } from './marketplace'

interface MarketplaceInfiniteProductLoaderProps {
  locale: Locales
  slug: string
  orderBy: string
  limit: number
  hidePriceSection?: boolean
  categoryUrlPrefix?: string
}

export function MarketplaceInfiniteProductLoader({
  locale,
  slug,
  orderBy,
  limit,
  hidePriceSection = false,
  categoryUrlPrefix,
}: MarketplaceInfiniteProductLoaderProps) {
  const [products, setProducts] = React.useState<MarketplaceCategoryProduct[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)
  const [isInitialized, setIsInitialized] = React.useState(false)
  const hasTriggeredInThisIntersection = React.useRef(false)
  const pageRef = React.useRef(1)

  const [ref, entry] = useIntersectionObserver<HTMLDivElement>()
  const isIntersecting = entry?.isIntersecting ?? false

  const loadPage = React.useCallback(
    async (pageNum: number) => {
      const normalizedOrderBy: SortType = orderBy === 'date' ? 'date' : 'popularity'
      const data = await getMarketplaceProducts({
        locale: locale as Language,
        slug,
        orderBy: setSortingOptions(normalizedOrderBy),
        limit,
        page: pageNum,
      })
      const payload = getMarketplaceProductsPayload(data)

      return {
        products: mapMarketplaceProductsToCategoryProducts(
          payload?.products,
          locale as Language,
          categoryUrlPrefix,
        ),
        totalCount: payload?.pagination?.totalCount || 0,
      }
    },
    [categoryUrlPrefix, limit, locale, orderBy, slug],
  )

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')

    if (pageParam !== null) {
      const urlPage = Number.parseInt(pageParam, 10)

      if (Number.isNaN(urlPage) || urlPage <= 1) {
        const url = new URL(window.location.href)
        url.searchParams.delete('page')
        window.history.replaceState({}, '', url.toString())
        setIsInitialized(true)
        return
      }

      pageRef.current = urlPage
      setIsLoading(true)

      loadPage(urlPage)
        .then(data => {
          if (data.products.length) {
            setProducts(data.products)
          }

          if (urlPage * limit >= data.totalCount) {
            setHasMore(false)
          }

          setIsLoading(false)
          setIsInitialized(true)
        })
        .catch(error => {
          Sentry.captureException(error)
          setIsLoading(false)
          setIsInitialized(true)
        })

      return
    }

    setIsInitialized(true)
  }, [limit, loadPage])

  const loadMore = React.useCallback(async () => {
    const nextPage = pageRef.current + 1
    setIsLoading(true)
    pageRef.current = nextPage

    const url = new URL(window.location.href)
    url.searchParams.set('page', String(nextPage))
    window.history.pushState({}, '', url.toString())

    try {
      const data = await loadPage(nextPage)

      if (data.products.length) {
        setProducts(prev => [...prev, ...data.products])
      }

      if (nextPage * limit >= data.totalCount) {
        setHasMore(false)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [limit, loadPage])

  React.useEffect(() => {
    if (!isIntersecting) {
      hasTriggeredInThisIntersection.current = false
    }
  }, [isIntersecting])

  React.useEffect(() => {
    if (
      isInitialized &&
      isIntersecting &&
      !hasTriggeredInThisIntersection.current &&
      hasMore &&
      !isLoading
    ) {
      hasTriggeredInThisIntersection.current = true
      void loadMore()
    }
  }, [hasMore, isInitialized, isIntersecting, isLoading, loadMore])

  if (products.length === 0 && !isLoading) {
    return <div ref={ref} className="h-px w-full bg-transparent" />
  }

  return (
    <>
      {products.map(product => (
        <ProductCard
          key={product.productId}
          productId={String(product.productId || 0)}
          productUrl={addOrReplaceLocaleInUrl(product.productUrl || '', locale as Language)}
          imageClassName="aspect-[3/2] object-cover"
          image={product.productImage || ''}
          name={product.productTitleLocalized?.[locale] || product.productTitle || ''}
          alt={product.imageAlt || ''}
          category={product.cardCategory?.[locale] || ''}
          categoryUrl={
            product.categoryUrl
              ? addOrReplaceLocaleInUrl(product.categoryUrl || '', locale as Language)
              : undefined
          }
          countdown={
            product.productCountdown
              ? new Date(product.productCountdown.replace(/\//g, '-')).getTime()
              : undefined
          }
          hidePriceSection={hidePriceSection}
          isLegacy
        />
      ))}
      <div ref={ref} className="h-px w-full bg-transparent" />
    </>
  )
}
